import React, { useState, useEffect } from "react";
import { Modal, Spin, Alert, Typography } from "antd";
import { InfoCircleOutlined, CloseOutlined } from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { getContentConfig } from "../../content/contentRegistry";
import { recordDocumentView } from "../../utils/helpDocumentTracker";
import { getHelpDocumentContent } from "../../api/helpDocumentApi";
import "./HelpModal.css";

const { Title } = Typography;



/**
 * 帮助文档显示模态框组件
 * 用于显示markdown格式的帮助文档、功能说明、更新日志等内容
 */
const HelpModal = ({
    visible,
    onClose,
    contentPath,
    title = "帮助文档",
    width = 900, // 增加默认宽度以更好显示图片
    ...modalProps
}) => {
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);

    // 加载markdown内容
    useEffect(() => {
        if (visible && contentPath) {
            loadContent();
            // 记录文档查看
            recordDocumentView(contentPath);
        }
    }, [visible, contentPath]);

    // 键盘事件监听
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && previewImage) {
                handleClosePreview();
            }
        };

        if (previewImage) {
            document.addEventListener('keydown', handleKeyDown);
            // 防止页面滚动
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [previewImage]);

    const loadContent = async () => {
        setLoading(true);
        setError(null);

        try {
            // 从后端API获取markdown内容
            const content = await getHelpDocumentContent(contentPath);
            setContent(content);
        } catch (err) {
            console.error("加载帮助内容失败:", err);
            setError(err.message || "加载内容失败");

            // 如果API失败，显示友好的错误信息
            const config = getContentConfig(contentPath);
            const documentTitle = config ? config.title : contentPath;
            setError(`无法加载文档"${documentTitle}"，请检查网络连接或联系管理员。`);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setContent("");
        setError(null);
        setPreviewImage(null);
        onClose();
    };

    // 处理图片点击
    const handleImageClick = (src) => {
        setPreviewImage(src);
    };

    // 关闭图片预览
    const handleClosePreview = () => {
        setPreviewImage(null);
    };

    // 自定义渲染器
    const customRenderers = {
        img: ({ src, alt, ...props }) => (
            <img
                src={src}
                alt={alt}
                onClick={() => handleImageClick(src)}
                style={{ cursor: 'pointer' }}
                {...props}
            />
        ),
        // 确保列表正确渲染
        ul: ({ children, ...props }) => (
            <ul style={{ paddingLeft: '20px', marginBottom: '16px' }} {...props}>
                {children}
            </ul>
        ),
        ol: ({ children, ...props }) => (
            <ol style={{ paddingLeft: '20px', marginBottom: '16px' }} {...props}>
                {children}
            </ol>
        ),
        li: ({ children, ...props }) => (
            <li style={{ marginBottom: '4px', lineHeight: '1.6' }} {...props}>
                {children}
            </li>
        ),
        // 确保段落正确渲染
        p: ({ children, ...props }) => (
            <p style={{ marginBottom: '12px', lineHeight: '1.6' }} {...props}>
                {children}
            </p>
        ),
        // 确保标题正确渲染
        h1: ({ children, ...props }) => (
            <h1 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px', color: '#1890ff' }} {...props}>
                {children}
            </h1>
        ),
        h2: ({ children, ...props }) => (
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px', color: '#262626' }} {...props}>
                {children}
            </h2>
        ),
        h3: ({ children, ...props }) => (
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#595959' }} {...props}>
                {children}
            </h3>
        )
    };

    return (
        <Modal
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <InfoCircleOutlined style={{ color: "#1890ff" }} />
                    <span>{title}</span>
                </div>
            }
            open={visible}
            onCancel={handleClose}
            footer={null}
            width={width}
            className="help-modal"
            destroyOnHidden={true}
            {...modalProps}
        >
            <div className="help-modal-content">
                {loading && (
                    <div style={{ textAlign: "center", padding: "40px 0" }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16, color: "#666" }}>加载中...</div>
                    </div>
                )}

                {error && (
                    <Alert
                        message="加载失败"
                        description={error}
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                {!loading && !error && content && (
                    <div className="markdown-content help-markdown">
                        <ReactMarkdown
                            components={customRenderers}
                            remarkPlugins={[remarkGfm, remarkBreaks]}
                        >
                            {content}
                        </ReactMarkdown>
                    </div>
                )}

                {!loading && !error && !content && (
                    <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
                        暂无内容
                    </div>
                )}
            </div>

            {/* 图片预览遮罩 */}
            {previewImage && (
                <div className="help-image-preview-overlay" onClick={handleClosePreview}>
                    <div className="help-image-preview-close" onClick={handleClosePreview}>
                        <CloseOutlined />
                    </div>
                    <img
                        src={previewImage}
                        alt="预览图片"
                        className="help-image-preview-content"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </Modal>
    );
};

export default HelpModal;
